import { useMemo, useCallback } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  useTrip,
  useGenerateItinerary,
  useShareTrip,
  useUnshareTrip,
  useUpdateTrip,
} from "./useTrips";
import { useGroupMembers, useCreateGroupForTrip, useGroup } from "./useGroups";
import { usePackingItems } from "./usePacking";
import { useTripMode, type TripMode } from "./useTripMode";
import { getTripCover } from "@/utils/trip-cover";
import { findTodaysItinerary, findNextSpot, getDestinationCoords } from "@/utils/trip-detail";
import type { GenerateContext } from "../../presentation/components/AiGenerateDialog";
import type {
  TripDetailResponseDto,
  PackingItemResponseDto,
  GroupMemberResponseDto,
  GroupResponseDto,
  ItineraryResponseDto,
  SpotResponseDto,
} from "@tabipla/shared";

// ==================== Types ====================

export interface UseTripDetailReturn {
  // Data
  trip: TripDetailResponseDto | undefined;
  packingItems: PackingItemResponseDto[];
  groupMembers: GroupMemberResponseDto[] | undefined;
  currentGroup: GroupResponseDto | undefined;

  // Loading/Error states
  isLoading: boolean;
  error: Error | null;

  // Computed values
  hasItinerary: boolean;
  coverEmoji: string;
  coverColor: string;
  todaysItinerary: ItineraryResponseDto | null;
  nextSpot: SpotResponseDto | null;

  // Trip mode
  tripMode: TripMode;
  daysUntilStart: number | null;

  // Mutations (for loading state access)
  mutations: {
    generate: ReturnType<typeof useGenerateItinerary>;
    share: ReturnType<typeof useShareTrip>;
    unshare: ReturnType<typeof useUnshareTrip>;
    updateTrip: ReturnType<typeof useUpdateTrip>;
    createGroupForTrip: ReturnType<typeof useCreateGroupForTrip>;
  };

  // Actions
  refetch: () => void;
  handleAiGenerate: (context: GenerateContext) => Promise<void>;
  handleSaveCover: (emoji: string, color: string) => Promise<void>;
  handleInvite: (openInviteDialog: () => void) => Promise<void>;
  handleShare: () => Promise<void>;
  handleUnshare: () => Promise<void>;

  // Helpers
  getAddSpotDestinationCoords: (
    itineraryId: string | null
  ) => { latitude: number; longitude: number } | null;
}

// ==================== Hook ====================

export function useTripDetail(tripId: string | undefined): UseTripDetailReturn {
  const { t } = useTranslation();

  // ==================== Data Fetching ====================
  const { data: trip, isLoading, error, refetch } = useTrip(tripId);
  const { data: packingItems = [] } = usePackingItems(tripId ?? "");
  const { data: groupMembers } = useGroupMembers(trip?.groupId ?? undefined);
  const { data: currentGroup } = useGroup(trip?.groupId ?? undefined);

  // ==================== Mutations ====================
  const generateMutation = useGenerateItinerary(tripId ?? "");
  const shareMutation = useShareTrip(tripId ?? "");
  const unshareMutation = useUnshareTrip(tripId ?? "");
  const updateTripMutation = useUpdateTrip(tripId ?? "");
  const createGroupForTripMutation = useCreateGroupForTrip(tripId ?? "");

  // ==================== Trip Mode ====================
  const { mode: tripMode, daysUntilStart } = useTripMode({
    startDate: trip?.startDate ?? null,
    endDate: trip?.endDate ?? null,
  });

  // ==================== Computed Values ====================
  const hasItinerary = useMemo(
    () => Boolean(trip?.itineraries && trip.itineraries.length > 0),
    [trip?.itineraries]
  );

  const { coverEmoji, coverColor } = useMemo(() => {
    if (!trip) return { coverEmoji: "", coverColor: "" };
    const generatedCover = getTripCover(trip.destination, trip.interests);
    return {
      coverEmoji: trip.coverEmoji ?? generatedCover.emoji,
      coverColor: trip.coverColor ?? generatedCover.gradient,
    };
  }, [trip]);

  const todaysItinerary = useMemo(
    () => findTodaysItinerary(trip?.itineraries, tripMode),
    [trip?.itineraries, tripMode]
  );

  const nextSpot = useMemo(() => findNextSpot(todaysItinerary), [todaysItinerary]);

  // ==================== Helper Functions ====================
  const getAddSpotDestinationCoords = useCallback(
    (itineraryId: string | null) => getDestinationCoords(itineraryId, trip?.itineraries),
    [trip?.itineraries]
  );

  // ==================== Actions ====================
  const handleAiGenerate = useCallback(
    async (context: GenerateContext) => {
      try {
        await generateMutation.mutateAsync(context);
        toast.success(t("trips.generateSuccess"));
      } catch {
        toast.error(t("trips.generateFailed"));
        throw new Error("Generation failed");
      }
    },
    [generateMutation, t]
  );

  const handleSaveCover = useCallback(
    async (emoji: string, color: string) => {
      await updateTripMutation.mutateAsync({
        coverEmoji: emoji,
        coverColor: color,
      });
    },
    [updateTripMutation]
  );

  const handleInvite = useCallback(
    async (openInviteDialog: () => void) => {
      if (!trip) return;

      if (!trip.groupId) {
        await createGroupForTripMutation.mutateAsync();
      }
      openInviteDialog();
    },
    [trip, createGroupForTripMutation]
  );

  const handleShare = useCallback(async () => {
    try {
      await shareMutation.mutateAsync();
      toast.success(t("share.linkCreated", "共有リンクを作成しました"));
    } catch {
      toast.error(t("share.createError", "共有リンクの作成に失敗しました"));
    }
  }, [shareMutation, t]);

  const handleUnshare = useCallback(async () => {
    try {
      await unshareMutation.mutateAsync();
      toast.success(t("share.linkDisabled", "共有を停止しました"));
    } catch {
      toast.error(t("share.disableError", "共有の停止に失敗しました"));
    }
  }, [unshareMutation, t]);

  return {
    // Data
    trip,
    packingItems,
    groupMembers,
    currentGroup,

    // Loading/Error states
    isLoading,
    error: error as Error | null,

    // Computed values
    hasItinerary,
    coverEmoji,
    coverColor,
    todaysItinerary,
    nextSpot,

    // Trip mode
    tripMode,
    daysUntilStart,

    // Mutations
    mutations: {
      generate: generateMutation,
      share: shareMutation,
      unshare: unshareMutation,
      updateTrip: updateTripMutation,
      createGroupForTrip: createGroupForTripMutation,
    },

    // Actions
    refetch,
    handleAiGenerate,
    handleSaveCover,
    handleInvite,
    handleShare,
    handleUnshare,

    // Helpers
    getAddSpotDestinationCoords,
  };
}
